import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LOOKUP_META, MAILING_TYPE_LABEL } from '@app/shared';
import type { ApiLogDto, CustomerDto } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { formatCents, formatDate, formatDateTime } from '../../lib/format';
import { APP_NAME } from '../../app/theme';
import { ReleasedFeature, useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { Can } from '../auth/guards';
import { useCustomerContext } from './CustomerLayout';
import { NotesSection } from './NotesSection';
import {
  BalanceSms,
  CancelAutoPay,
  CurrentShippingInfo,
  EsimActivationCode,
  PortoutLock,
} from './CustomerPageDialogs';
import { DidValue, FlagSwitches, PinValue, PortalPassword } from './ProfileExtras';
import { useCustomerApiLog, useUpdatePreferences, useVerifyEmail } from './api';
import type { Preferences } from './api';
import { Field, FieldGrid, Section, statusSentence, streetOf, userLabel, yesNo } from './parts';
import {
  ClassificationField,
  CollectionProtectionField,
  CollectionStatusField,
  PlanCustomExpiry,
  SuppressBillsField,
  Winback,
} from './AccountDialogs';

/** The four contact switches, with the reference's labels. */
const SWITCHES: Array<[keyof Preferences, string]> = [
  ['stopPromoSms', 'Stop Promotional SMS Notification'],
  ['stopPromoEmail', 'Stop Promotional Email Notification'],
  ['stopTxnSms', 'Stop Transactional SMS Notification'],
  ['stopTxnEmail', 'Stop Transactional Email Notification'],
];

/** The switches save as soon as one is moved. Without PC805 they are shown as plain values. */
function PreferenceSwitches({ customer }: { customer: CustomerDto }) {
  const { can } = useAuth();
  const notify = useNotify();
  const update = useUpdatePreferences(customer.id);

  if (!can('PC805'))
    return (
      <>
        {SWITCHES.map(([key, label]) => (
          <Field key={key} label={label} value={yesNo(customer.preferences[key])} />
        ))}
      </>
    );

  const toggle = async (key: keyof Preferences, value: boolean) => {
    try {
      await update.mutateAsync({ ...customer.preferences, [key]: value });
      notify.success('Notification preference saved.');
    } catch (err) {
      notify.error(err);
    }
  };
  return (
    <>
      {SWITCHES.map(([key, label]) => (
        <FormControlLabel
          key={key}
          label={label}
          control={
            <Switch
              size="small"
              checked={customer.preferences[key]}
              disabled={update.isPending}
              onChange={(e) => void toggle(key, e.target.checked)}
            />
          }
        />
      ))}
    </>
  );
}

/** A calendar date stored as YYYY-MM-DD, shown as MM/DD/YYYY without any time zone shift. */
const formatDay = (day: string | null): string => {
  const [y, m, d] = (day ?? '').split('-');
  return y && m && d ? `${m}/${d}/${y}` : '';
};

const json = (value: Record<string, unknown>) => (
  <Box component="code" sx={{ fontSize: 12, overflowWrap: 'anywhere' }}>
    {JSON.stringify(value)}
  </Box>
);

const API_LOG_COLUMNS: Column<ApiLogDto>[] = [
  { key: '#', header: '#', width: 50, render: (_l, i) => i + 1 },
  { key: 'api', header: 'Api', nowrap: true, render: (l) => `${l.api} / ${l.action}` },
  { key: 'request', header: 'Request', render: (l) => json(l.request) },
  { key: 'response', header: 'Response', render: (l) => json(l.response) },
  { key: 'at', header: 'Date Time', nowrap: true, render: (l) => formatDateTime(l.at) },
];

/** Carrier calls made for this customer, closed until asked for. Loads when first opened. */
function ApiLogPanel({ customerId }: { customerId: string }) {
  const [open, setOpen] = useState(false);
  const log = useCustomerApiLog(customerId, open);
  return (
    <Accordion
      expanded={open}
      onChange={(_e, expanded) => setOpen(expanded)}
      disableGutters
      elevation={0}
      variant="outlined"
      sx={{ mt: 1.5 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600 }}>API Log</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1 }}>
        {open ? (
          <DataTable
            label="API Log"
            columns={API_LOG_COLUMNS}
            rows={log.data}
            rowKey={(l) => l.id}
            loading={log.isFetching}
            error={log.error}
            onRetry={() => void log.refetch()}
          />
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * Customer Profile (PC56). Sections and labels follow the reference; fields it has that this
 * build does not store are left out (see docs/technical/customers.md).
 */
/**
 * The Email value with the reference's "Verify Email" button (PC377 is asked for; the
 * reference's permission was not seen). After the mail is sent its message is shown and the
 * button goes away for this visit, as in the reference.
 */
function EmailValue({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const verify = useVerifyEmail(customer.id);
  const [sent, setSent] = useState(false);
  const send = async () => {
    try {
      const res = await verify.mutateAsync(undefined);
      notify.success(res.message);
      setSent(true);
    } catch (err) {
      notify.error(err);
    }
  };
  return (
    <>
      {customer.email}
      {customer.email && !sent ? (
        <ReleasedFeature name="verify-email">
          <Can code="PC377">
            <Button
              size="small"
              variant="outlined"
              sx={{ ml: 1 }}
              disabled={verify.isPending}
              onClick={() => void send()}
            >
              Verify Email
            </Button>
          </Can>
        </ReleasedFeature>
      ) : null}
    </>
  );
}

export function CustomerProfilePage() {
  const { customer: c } = useCustomerContext();
  const { can } = useAuth();
  const featureOn = useFeatureReleased();
  // The rows read from other modules (release feature "profile-rows").
  const rows = featureOn('profile-rows');
  const sentence = statusSentence(c);
  const { line, other } = c;
  // The reason belongs to the last suspension or disconnection.
  const reasonLabel =
    c.status === 'Suspended'
      ? LOOKUP_META.suspendReason.title
      : c.status === 'Inactive'
        ? LOOKUP_META.disconnectReason.title
        : null;

  return (
    <Box>
      {sentence ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {sentence}
        </Alert>
      ) : null}

      <Section title="Customer Information">
        <FieldGrid>
          <Field label="Service Address" value={streetOf(c.serviceAddress)} />
          <Field label="City" value={c.serviceAddress.city} />
          <Field label="State" value={c.serviceAddress.state} />
          <Field label="Zip" value={c.serviceAddress.zip} />
          {rows ? <Field label="PIN" value={<PinValue customer={c} />} /> : null}
          <Field label="Alternate Ph" value={c.alternateNumber} />
          <Field label="Email" value={<EmailValue customer={c} />} />
          <Field
            label="Mailing Address"
            value={[
              streetOf(c.mailingAddress),
              c.mailingAddress.city,
              c.mailingAddress.state,
              c.mailingAddress.zip,
            ]
              .filter(Boolean)
              .join(', ')}
          />
          <Field label="Address Type" value={MAILING_TYPE_LABEL[c.mailingAddress.type]} />
          <Field label="Account #" value={c.accountNo} />
          <Field label="Language" value={c.language} />
          <Field label="Secret Ques" value={c.secretQuestion} />
          <Field label="Secret Ans" value={c.secretAnswer} />
          {c.identity.visible ? (
            <>
              <Field
                label="Customer SSN (PC253)"
                value={c.identity.ssnLast4 ? `***-**-${c.identity.ssnLast4}` : ''}
              />
              <Field label="Customer DOB (PC253)" value={formatDay(c.identity.dob)} />
            </>
          ) : null}
          <Field label="Subscriber ID" value={c.subscriberId} />
          <Field label="A/U Name" value={c.authorizedUser.name} />
          <Field label="A/U Password" value={c.authorizedUser.password} />
          <Field label="A/U S/Q" value={c.authorizedUser.secretQuestion} />
          <Field label="A/U Relationship" value={c.authorizedUser.relationship} />
          {c.authorizedUser2.name ? (
            <Field
              label="A/U Name (User2)"
              value={[
                c.authorizedUser2.name,
                c.authorizedUser2.relationship ? `(${c.authorizedUser2.relationship})` : '',
              ]
                .filter(Boolean)
                .join(' ')}
            />
          ) : null}
          {rows ? (
            <>
              <Field label="DID No" value={<DidValue customer={c} />} />
              <Field label="Company" value={c.company} />
              <Field label="Current Balance" value={formatCents(c.currentBalanceCents)} />
            </>
          ) : null}
          <Field label="Tag #" value={c.tags.join(', ')} />
        </FieldGrid>
        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gap: 1,
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          }}
        >
          <PreferenceSwitches customer={c} />
          <ReleasedFeature name="profile-switches">
            <FlagSwitches customer={c} />
          </ReleasedFeature>
        </Box>
      </Section>

      <Section
        title="Line Information"
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <ReleasedFeature name="current-shipping-info">
              <CurrentShippingInfo customer={c} />
            </ReleasedFeature>
            <ReleasedFeature name="esim-qr-code">
              <EsimActivationCode customer={c} />
            </ReleasedFeature>
            <ReleasedFeature name="portout-lock">
              <PortoutLock customer={c} />
            </ReleasedFeature>
            <ReleasedFeature name="customer-portal">
              <PortalPassword customer={c} />
            </ReleasedFeature>
            <ReleasedFeature name="customer-account-dialogs">
              <Winback customer={c} />
            </ReleasedFeature>
            <ReleasedFeature name="add-line">
              <Can code="PC543">
                {/* The reference opens Enrollment step 1 with the ZIP filled in; whether the new line is tied to this account there was not seen. */}
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/enrollments/new?zip=${encodeURIComponent(c.serviceAddress.zip)}`}
                >
                  Add Line (PC543)
                </Button>
              </Can>
            </ReleasedFeature>
          </Box>
        }
      >
        <FieldGrid>
          <Field label="Telephone" value={line.mdn} />
          <Field label="SIM/ESN" value={line.sim} />
          <Field label="Make & Model" value={line.makeModel} />
          <Field label="eSIM" value={line.isEsim ? 'YES' : 'NO'} />
          {rows ? <Field label="Ported MDN" value={line.portedMdn ? 'YES' : 'NO'} /> : null}
          <Field label="Wifi Calling" value={yesNo(line.wifiCalling)} />
          <Field label="Acc. Type" value={line.accountType} />
          <Field
            label="Carrier"
            value={line.carrier ? `${line.carrier.name} (${line.carrier.code})` : ''}
          />
          {/* The reference names this row after its own product ("<product> Plan"). */}
          <Field
            label={`${APP_NAME} Plan`}
            value={
              <>
                {line.plan?.name}
                {line.plan && rows ? (
                  <Can code="PC77">
                    <Button size="small" component={Link} to="plan-change" sx={{ ml: 1 }}>
                      Change Plan
                    </Button>
                  </Can>
                ) : null}
              </>
            }
          />
          {rows ? <Field label="Plan Details" value={line.planDetails} /> : null}
          <Field label="Plan Price" value={line.plan ? formatCents(line.plan.priceCents) : ''} />
          {rows ? (
            <Field
              label="Query Usage"
              value={
                line.carrier ? (
                  can('PC254') ? (
                    <Link to="usage">{line.carrier.code}</Link>
                  ) : (
                    line.carrier.code
                  )
                ) : (
                  ''
                )
              }
            />
          ) : null}
        </FieldGrid>
        <ApiLogPanel customerId={c.id} />
      </Section>

      <Section title="Other Information">
        <FieldGrid>
          <Field
            label="Wallet Balance"
            value={
              <>
                {formatCents(other.walletBalanceCents)}
                <ReleasedFeature name="balance-sms">
                  <BalanceSms customer={c} />
                </ReleasedFeature>
              </>
            }
          />
          <Field label="Order by" value={userLabel(other.orderBy)} />
          {rows ? (
            <>
              <Field label="Master Agent ID" value={other.masterAgent?.loginId ?? ''} />
              <Field label="Agent Name" value={other.orderBy?.name ?? ''} />
            </>
          ) : (
            <Field label="Master Agent" value={userLabel(other.masterAgent)} />
          )}
          <Field label="Enrollment ID" value={c.enrollmentId} />
          {rows ? <Field label="Approved by" value={userLabel(other.approvedBy)} /> : null}
          <Field label="Source" value={other.source} />
          {rows ? (
            <>
              <Field
                label="Application Approval"
                value={formatDateTime(other.applicationApprovalAt)}
              />
              <Field
                label="Non-Lifeline Activation"
                value={formatDateTime(other.nonLifelineActivationAt)}
              />
            </>
          ) : null}
          <Field
            label="Plan Expiry Date"
            value={
              <>
                {formatDate(line.planExpiresAt)}
                <ReleasedFeature name="customer-account-dialogs">
                  <PlanCustomExpiry customer={c} />
                </ReleasedFeature>
              </>
            }
          />
          <ReleasedFeature name="customer-account-dialogs">
            <ClassificationField customer={c} />
            <CollectionProtectionField customer={c} />
            <SuppressBillsField customer={c} />
          </ReleasedFeature>
          <ReleasedFeature name="collection-status">
            <CollectionStatusField customer={c} />
          </ReleasedFeature>
          {rows ? (
            <>
              <Field
                label="Will the Prepaid plan check for Autopay"
                value={
                  <>
                    {yesNo(other.expiry.checksAutopay)}
                    <ReleasedFeature name="cancel-autopay">
                      <CancelAutoPay customer={c} />
                    </ReleasedFeature>
                  </>
                }
              />
              <Field
                label="Will the prepaid plan check for Wallet balance"
                value={yesNo(other.expiry.checksWallet)}
              />
              <Field label="Plan expired move to" value={other.expiry.planExpiredMoveTo} />
              <Field
                label="Grace period expiry date"
                value={formatDate(other.expiry.graceExpiresAt)}
              />
              <Field label="Grace period expired move to" value={other.expiry.graceExpiredMoveTo} />
            </>
          ) : null}
          {c.status === 'Suspended' && featureOn('profile-suspended-date') ? (
            <Field label="Suspended" value={formatDate(other.suspendedAt)} />
          ) : null}
          <Field label="Activation date" value={formatDate(other.activatedAt)} />
          <Field label="Disconnection date" value={formatDate(other.disconnectedAt)} />
          {reasonLabel ? <Field label={reasonLabel} value={c.statusReason} /> : null}
        </FieldGrid>
      </Section>

      <NotesSection customerId={c.id} customerNo={c.customerId} />
    </Box>
  );
}
